import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { EMPTY_FILTERS, filterOptionsQuery, hostTreeQuery, isAgentlessList, tablePageQuery, unassignedTallyQuery, type Filters, type ListName } from '../api/inventoree'
import type { Named } from '../api/shell'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { DeviceWorkspace } from '../devices/DeviceWorkspace'
import { AgentlessWorkspace } from './AgentlessWorkspace'
import { agentlessColumns, deviceColumns, mobileColumns, type DeviceRow } from './columns'
import { HostTreeView, useHostTreeMode } from './HostTree'
import { BlankQrModal, CsvModal, NewDeviceModal, SingleQrModal, useRowActions } from './ListWrites'
import { SelectedDevicesWidget } from './ListSettings'
import { useUnassignedBand } from './UnassignedBand'
import { UnassignedAssignBar, UnassignedCount, useAssignCell } from './UnassignedFirst'

/**
 * Listele Inventoree - Workstations, Servers, Mobiles, Unreachable,
 * Decommissioned, Unmanaged, Network, Other - cu machetele vechi
 * (inventoree/devices/*.php): acelasi tabel, aceleasi coloane, aceleasi
 * filtre, acelasi widget de selectie multipla.
 */

export const TABLE_ID: Record<ListName, string> = {
  workstations: 'inventoreeWorkstations',
  servers: 'inventoreeServers',
  inactive: 'inventoreeInactive',
  decommissioned: 'inventoreeDecommissioned',
  mobiles: 'inventoreeMobiles',
  unmanaged: 'inventoreeUnmanaged',
  network: 'inventoreeNetwork',
  agentless: 'inventoreeAgentless',
}

/** loadFilters(_table, 1) numai pe listele cu masini in viata: aici apare bifa "Inactive devices". */
const INACTIVE_SWITCH: Record<ListName, boolean> = {
  workstations: true,
  servers: true,
  inactive: false,
  decommissioned: false,
  mobiles: true,
  unmanaged: false,
  network: false,
  agentless: false,
}

const HOST_TREE: Record<ListName, boolean> = {
  workstations: true,
  servers: true,
  inactive: true,
  decommissioned: true,
  mobiles: false,
  unmanaged: false,
  network: false,
  agentless: false,
}

function Select({ label, name, options, value, onChange }: { label: string; name: string; options: Named[]; value: string; onChange(v: string): void }) {
  if (options.length === 0) return null
  return (
    <div className="col-md-4">
      <label>{label}</label>
      <select className="form-control input-sm" name={name} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select...</option>
        {options.map((o) => (
          <option key={o.id} value={String(o.id)}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  )
}

/** Switchery(size: 'small', color: '#0090D9', secondaryColor: '#DDDDDD') fara Switchery. */
function Switchery({ checked, onChange }: { checked: boolean; onChange(v: boolean): void }) {
  return (
    <>
      <input type="checkbox" className="switchery" name="inactive" value="1" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ display: 'none' }} />
      <span
        className="switchery switchery-small"
        role="switch"
        aria-checked={checked}
        tabIndex={0}
        onClick={() => onChange(!checked)}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault()
            onChange(!checked)
          }
        }}
        style={{
          backgroundColor: checked ? 'rgb(0, 144, 217)' : 'rgb(255, 255, 255)',
          borderColor: checked ? 'rgb(0, 144, 217)' : 'rgb(221, 221, 221)',
          boxShadow: checked ? 'rgb(0, 144, 217) 0px 0px 0px 16px inset' : 'rgb(221, 221, 221) 0px 0px 0px 0px inset',
          transition: 'border 0.4s, box-shadow 0.4s, background-color 1.2s',
        }}
      >
        <small style={{ left: checked ? 13 : 0, transition: 'background-color 0.4s, left 0.2s' }}></small>
      </span>
    </>
  )
}

function FiltersPanel({ list, applied, onApply, close }: { list: ListName; applied: Filters; onApply(f: Filters): void; close(): void }) {
  const options = useQuery(filterOptionsQuery)
  const [draft, setDraft] = useState<Filters>(applied)
  const o = options.data
  const set = (key: keyof Filters) => (v: string) => setDraft((d) => ({ ...d, [key]: v }))

  return (
    <>
      <header className="panel-heading sm bg-default">
        <h4>
          <i className="fa fa-filter"></i> <strong>Apply</strong> filters
        </h4>
      </header>
      <div className="panel-body">
        <form
          className="form-horizontal labelcustomize tableFilters"
          data-collabel="3"
          data-alignlabel="left"
          data-label="color"
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            onApply(draft)
          }}
          onReset={(e) => {
            e.preventDefault()
            setDraft(EMPTY_FILTERS)
          }}
        >
          <div className="form-group">
            <Select label="Office" name="office" options={o?.offices ?? []} value={draft.office} onChange={set('office')} />
            <Select label="Brand" name="brand" options={o?.brands ?? []} value={draft.brand} onChange={set('brand')} />
            <Select label="Department" name="department" options={o?.departments ?? []} value={draft.department} onChange={set('department')} />
          </div>
          <div className="form-group">
            <Select label="Group" name="group" options={o?.groups ?? []} value={draft.group} onChange={set('group')} />
            <Select label="Owner" name="owner" options={o?.owners ?? []} value={draft.owner} onChange={set('owner')} />
            {INACTIVE_SWITCH[list] && (
              <div className="col-md-4">
                <label className="col-sm-12">Inactive devices</label>
                <div className="col-sm-12">
                  <Switchery checked={draft.inactive} onChange={(v) => setDraft((d) => ({ ...d, inactive: v }))} />
                </div>
              </div>
            )}
          </div>
          <div className="form-group offset">
            <div className="col-md-offset-5 col-md-7">
              <button type="submit" className="btn btn-theme applyFilters">
                Apply
              </button>{' '}
              <button type="reset" className="btn btn-inverse">
                Reset
              </button>{' '}
              <button type="button" className="btn closeFilters" onClick={close}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export function DeviceListPage({ list }: { list: ListName }) {
  const id = TABLE_ID[list]
  const [state, setState] = useTableState(id, { start: 0, length: 25, order: { column: 2, dir: 'desc' }, search: '' })
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [view, setView] = useState<'list' | 'tree'>(() => (sessionStorage.getItem(`od-host-view:${id}`) === 'tree' ? 'tree' : 'list'))
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [widgetHidden, setWidgetHidden] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [csvOpen, setCsvOpen] = useState(false)
  const [qrOpen, setQrOpen] = useState(false)
  const rowActions = useRowActions(() => void query.refetch())
  /* Cate aparate stau nealocate, si ce are omul voie cu ele: insigna din antet
     si butonul Assign din randul desfacut atarna amandoua de aici. */
  const tally = useQuery(unassignedTallyQuery)
  /* Butonul Assign din CELULA de proprietar, in locul textului "Unassigned":
     acelasi drept si aceeasi fereastra ca banda din randul desfacut. */
  const assignCell = useAssignCell(tally.data, () => void query.refetch())
  const columns = useMemo(() => {
    if (list === 'mobiles') return mobileColumns(undefined, assignCell.canAssign)
    if (isAgentlessList(list)) return agentlessColumns(assignCell.canAssign)
    return deviceColumns(undefined, assignCell.canAssign)
  }, [list, assignCell.canAssign])

  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[2]
  const query = useQuery({
    ...tablePageQuery({
      list,
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
      filters,
    }),
    placeholderData: keepPreviousData,
  })
  /* Banda rosie din capul tabelului si insigna rosie din antet: statiile
     fara utilizator din scopul listei (UnassignedBand). */
  const unknown = useUnassignedBand(query.data?.unassigned, tally.data, () => void query.refetch())

  const rows = (query.data?.data ?? []) as DeviceRow[]
  const activeFilters = Object.entries(filters).filter(([k, v]) => (k === 'inactive' ? v === true : v !== '')).length

  /* Bifele de selectie (DataTablesMultiselect): capul bifeaza toate, un rand bifat arata widgetul. */
  const toggleAll = (on: boolean) => setSelected(on ? new Set(rows.map((r) => r.deviceId)) : new Set())
  const onTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target instanceof HTMLInputElement && target.classList.contains('oneDevice')) {
      const idNum = Number(target.value)
      setSelected((s) => {
        const next = new Set(s)
        if (target.checked) next.add(idNum)
        else next.delete(idNum)
        return next
      })
      setWidgetHidden(false)
      return
    }
    if (target instanceof HTMLInputElement && target.classList.contains('allDevices')) {
      toggleAll(target.checked)
      setWidgetHidden(false)
      return
    }
    if (assignCell.onAssignCellClick(e)) return
    rowActions.onRowActionClick(e)
  }

  const switchView = (next: 'list' | 'tree') => {
    setView(next)
    sessionStorage.setItem(`od-host-view:${id}`, next)
  }
  useHostTreeMode(HOST_TREE[list] && view === 'tree')
  const tree = useQuery({
    ...hostTreeQuery({ list, search: state.search, filters }),
    enabled: HOST_TREE[list] && view === 'tree',
  })

  return (
    <Content>
      {assignCell.modal}
      <div onClick={onTableClick}>
        <DataTable
          id={id}
          columns={columns}
          rows={rows.map((r) => ({ ...r, __checked: selected.has(r.deviceId) }))}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          wrapperClass={HOST_TREE[list] && view === 'tree' ? 'od-host-tree-on' : undefined}
          rowId={(r) => String(r.DT_RowId)}
          // `DataTablesProperties.buttons` (general.js), pe care portalul le pune pe
          // fiecare lista de aparate: Copy, Columns si Refresh (`dt.ajax.reload(null,
          // false)`) stau in subsol, dupa paginare.
          footerButtons={{ copy: true, colvis: true, refresh: () => void query.refetch() }}
          columnTools
          details={(row) =>
            isAgentlessList(list) ? (
              <AgentlessWorkspace id={Number(row.deviceId)} />
            ) : (
              <>
                {/* Aparatul n-are stapan: banda cu Assign, deasupra fisei. */}
                <UnassignedAssignBar
                  id={Number(row.deviceId)}
                  row={row}
                  tally={tally.data}
                  onAssigned={() => void query.refetch()}
                />
                <DeviceWorkspace id={Number(row.deviceId)} from="inventoree" />
              </>
            )
          }
          headerButtons={
            <>
              {/* "N unassigned" duce la lista Unknown. Lipseste cand nu e
                  niciunul sau cand omul n-are drept acolo. */}
              {unknown.badge ?? <UnassignedCount tally={tally.data} />}
              {/*
                Ordinea si clasele sunt cele din `agentlessButtons`
                (public/js/inventoree.js): ADD Device verde, Import CSV (Blank
                QR a coborat in subsol). `tk-header-extra` e clasa pe care le-o punea
                `list_chrome.js` cand le scotea din banda de deasupra tabelului
                si le urca in bara de sus - pe ea sta masura lor de acolo, si
                tot ea le tine pe acelasi rand cu cautarea.
              */}
              {isAgentlessList(list) && (
                <>
                  <a
                    className="btn btn-success addDevice tk-header-extra"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      setAddOpen(true)
                    }}
                  >
                    <i className="fas fa-plus"></i> ADD Device
                  </a>
                  <a
                    className="btn btn-default csv tk-header-extra"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      setCsvOpen(true)
                    }}
                  >
                    <i className="fa fa-upload"></i> Import CSV
                  </a>
                </>
              )}
            </>
          }
          footerButtonsExtra={
            /* Blank QR a coborat in subsol, langa Copy/PDF/Excel/Refresh, ca
               sa-i faca loc lui Columns in bara de sus (14 septembrie 2026). */
            <a
              className="btn btn-default qr tk-header-extra"
              tabIndex={0}
              aria-controls={id}
              href="#"
              data-device={0}
              data-target="#exportBlankQRModal"
              onClick={(e) => {
                e.preventDefault()
                setQrOpen(true)
              }}
            >
              <span>
                <i className="fa fa-qrcode"></i> Blank QR
              </span>
            </a>
          }
          headerView={
            HOST_TREE[list] ? (
              <div className="tk-view-toggle btn-group tk-header-view" role="group" aria-label="List or Tree View">
                <button type="button" className={`btn btn-default tk-view-list${view === 'list' ? ' active' : ''}`} data-view="list" title="List" onClick={() => switchView('list')}>
                  <i className="fas fa-list" aria-hidden="true"></i> List
                </button>
                <button type="button" className={`btn btn-default tk-view-tree${view === 'tree' ? ' active' : ''}`} data-view="tree" title="Tree View" onClick={() => switchView('tree')}>
                  <i className="fas fa-sitemap" aria-hidden="true"></i> Tree View
                </button>
              </div>
            ) : undefined
          }
          filters={{
            active: activeFilters,
            render: (close) => (
              <FiltersPanel
                list={list}
                applied={filters}
                close={close}
                onApply={(f) => {
                  setFilters(f)
                  setState({ start: 0 })
                }}
              />
            ),
          }}
        >
          <SelectedDevicesWidget
            shown={selected.size > 0 && !widgetHidden}
            ids={[...selected]}
            onHide={() => setWidgetHidden(true)}
            settings={isAgentlessList(list) ? 'agentless' : 'properties'}
          />
          {unknown.band}
        </DataTable>
        {isAgentlessList(list) && (
          <>
            <NewDeviceModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={() => void query.refetch()} />
            <CsvModal open={csvOpen} onClose={() => setCsvOpen(false)} onSaved={() => void query.refetch()} />
          </>
        )}
        <BlankQrModal open={qrOpen} onClose={() => setQrOpen(false)} />
        <SingleQrModal device={rowActions.qrDevice} onClose={rowActions.closeQr} />
        {HOST_TREE[list] && view === 'tree' && (
          <HostTreeView
            roots={tree.data?.roots ?? []}
            loading={tree.isFetching && !tree.data}
            error={tree.isError ? (tree.error as Error).message : null}
          />
        )}
      </div>
    </Content>
  )
}
